export default function UseCases() {
  const cases = [
    {
      icon: 'ri-building-line',
      gradient: 'from-teal-500 to-cyan-600',
      category: 'Enterprise Data Center',
      title: 'Multi-Site DC Network Modernization',
      challenge: 'Legacy infrastructure across 5 data centers with inconsistent configurations, manual provisioning taking weeks, and frequent outages affecting business operations.',
      solution: 'Deployed automated assessment tools to analyze 2,500+ network devices, implemented Cisco ACI fabric with NDFC orchestration, and established end-to-end automation workflows.',
      results: [
        'Reduced provisioning time from 3 weeks to 2 hours',
        'Achieved 99.99% uptime across all sites',
        'Eliminated 85% of configuration errors',
        'Saved $2M annually in operational costs'
      ],
      metrics: {
        devices: '2,500+',
        uptime: '99.99%',
        savings: '$2M'
      }
    },
    {
      icon: 'ri-global-line',
      gradient: 'from-cyan-500 to-teal-600',
      category: 'Global SDWAN Deployment',
      title: 'Remote Office Network Transformation',
      challenge: 'Managing 150+ remote offices with expensive MPLS circuits, poor application performance, and limited visibility into network health and security posture.',
      solution: 'Conducted comprehensive remote office assessment, designed and deployed SDWAN with SASE integration, implemented centralized management and zero-touch provisioning.',
      results: [
        'Reduced WAN costs by 60% replacing MPLS',
        'Improved application performance by 3x',
        'Deployed 150 sites in 90 days',
        'Enhanced security with unified threat management'
      ],
      metrics: {
        sites: '150+',
        cost_reduction: '60%',
        performance: '3x'
      }
    },
    {
      icon: 'ri-cloud-line',
      gradient: 'from-teal-600 to-emerald-600',
      category: 'Cloud Migration',
      title: 'Hybrid Cloud Infrastructure Assessment',
      challenge: 'Complex on-premises infrastructure with plans to migrate to hybrid cloud, but lacking visibility into dependencies, performance bottlenecks, and migration risks.',
      solution: 'Performed automated cloud infrastructure assessment, mapped application dependencies, designed hybrid connectivity with F5 load balancing, and executed phased migration strategy.',
      results: [
        'Migrated 200+ applications with zero downtime',
        'Identified and resolved 50+ critical dependencies',
        'Optimized cloud costs by 40%',
        'Reduced migration timeline by 6 months'
      ],
      metrics: {
        applications: '200+',
        downtime: '0 hrs',
        timeline: '-6 mo'
      }
    },
    {
      icon: 'ri-shield-check-line',
      gradient: 'from-cyan-600 to-teal-700',
      category: 'Network Security',
      title: 'DDI Infrastructure Overhaul',
      challenge: 'Fragmented DNS, DHCP, and IPAM management across multiple platforms causing IP conflicts, security vulnerabilities, and compliance issues.',
      solution: 'Deployed Infoblox DDI solution with automated discovery, consolidated IP address management, implemented DNS security extensions, and established governance policies.',
      results: [
        'Eliminated 100% of IP conflicts',
        'Reduced DNS query response time by 70%',
        'Achieved compliance with security standards',
        'Automated 90% of IP address management tasks'
      ],
      metrics: {
        conflicts: '0',
        response_time: '-70%',
        automation: '90%'
      }
    },
    {
      icon: 'ri-server-line',
      gradient: 'from-emerald-600 to-teal-600',
      category: 'Load Balancer Migration',
      title: 'F5 to AVI Load Balancer Migration',
      challenge: 'Aging F5 infrastructure with high licensing costs, limited scalability, and complex management requiring specialized expertise.',
      solution: 'Assessed existing F5 configurations, designed AVI architecture with software-defined approach, automated migration of 500+ virtual services, and implemented analytics-driven optimization.',
      results: [
        'Migrated 500+ services with zero impact',
        'Reduced licensing costs by 50%',
        'Improved deployment speed by 10x',
        'Enhanced visibility with real-time analytics'
      ],
      metrics: {
        services: '500+',
        cost_savings: '50%',
        speed: '10x'
      }
    },
    {
      icon: 'ri-database-2-line',
      gradient: 'from-teal-700 to-cyan-700',
      category: 'Data Center Build',
      title: 'Greenfield Data Center Deployment',
      challenge: 'Building new state-of-the-art data center from scratch requiring modern architecture, automation-first approach, and seamless integration with existing infrastructure.',
      solution: 'Designed spine-leaf architecture with Arista switches, implemented network automation from day one, deployed comprehensive monitoring, and executed parallel run with legacy DC.',
      results: [
        'Completed build in 4 months',
        'Achieved 100% automation coverage',
        'Zero-touch provisioning for all devices',
        'Seamless cutover with no business impact'
      ],
      metrics: {
        timeline: '4 mo',
        automation: '100%',
        impact: '0'
      }
    }
  ];

  return (
    <section id="use-cases" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="text-teal-600 text-sm font-semibold tracking-wider uppercase mb-3">
            SUCCESS STORIES
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Real-World Network<br />Transformation Use Cases
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover how we've helped organizations modernize their network infrastructure, reduce costs, and achieve operational excellence
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {cases.map((useCase, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group border border-gray-100"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className={`w-14 h-14 rounded-lg bg-gradient-to-br ${useCase.gradient} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <i className={`${useCase.icon} text-2xl text-white`}></i>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-teal-600 uppercase tracking-wider mb-1">
                      {useCase.category}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{useCase.title}</h3>
                  </div>
                </div>
              </div>

              {/* Challenge */}
              <div className="mb-6">
                <div className="flex items-center mb-2">
                  <i className="ri-error-warning-line text-orange-500 mr-2"></i>
                  <h4 className="text-sm font-bold text-gray-900">Challenge</h4>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed pl-6">
                  {useCase.challenge}
                </p>
              </div>

              {/* Solution */}
              <div className="mb-6">
                <div className="flex items-center mb-2">
                  <i className="ri-lightbulb-line text-teal-600 mr-2"></i>
                  <h4 className="text-sm font-bold text-gray-900">Solution</h4>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed pl-6">
                  {useCase.solution}
                </p>
              </div>

              {/* Results */}
              <div className="mb-6">
                <div className="flex items-center mb-3">
                  <i className="ri-trophy-line text-emerald-600 mr-2"></i>
                  <h4 className="text-sm font-bold text-gray-900">Results</h4>
                </div>
                <ul className="space-y-2 pl-6">
                  {useCase.results.map((result, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-700">
                      <i className="ri-check-line text-emerald-600 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>{result}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-100">
                {Object.entries(useCase.metrics).map(([key, value], idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-2xl font-bold text-teal-600 mb-1">{value}</div>
                    <div className="text-xs text-gray-500 capitalize">{key.replace('_', ' ')}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl p-12">
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Transform Your Network?
          </h3>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Let us assess your infrastructure and design a solution tailored to your business needs
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap shadow-lg">
              Schedule Assessment
            </button>
            <button className="px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap">
              Download Case Studies
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
